// src/features/tasks/components/TaskDetailsPage/TaskEquipmentCard.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Stack,
  Button,
  IconButton,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Block as BlockIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Room as RoomIcon,
  Sync as SyncIcon,
  Biotech,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useTaskEquipment } from '../../hooks/useTaskEquipment';
import { getEquipmentImageByModel } from '../../../equipment/utils/getEquipmentImage';
import AddEquipmentDialog from './AddEquipmentDialog';
import EditEquipmentDialog from './EditEquipmentDialog';
import { useTaskPermissions } from '../../../auth/hooks/usePermissions';
import SectionHeader from '../../../../components/common/SectionHeader';
import { groupEquipmentByRoom } from '../../utils/equipmentGrouping';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';

const LOCKED_TASK_STATES = ['COMPLETED', 'NOT_COMPLETED'];

// Hidden while the task has no equipment (the page offers "Tilføj udstyr"
// instead); the add dialog is controlled by the page for that reason.
export default function TaskEquipmentCard({ task, onViewEquipment, addOpen, onAddOpen, onAddClose, onCountChange, ...props }) {
  const { t } = useTranslation(['tasks', 'equipment', 'common']);
  const { canModifyEquipment } = useTaskPermissions();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const {
    loading,
    error,
    equipment,
    listTaskEquipment,
    linkEquipmentToTask,
    updateTaskEquipment,
    setEquipmentChecked,
    unlinkEquipmentFromTask
  } = useTaskEquipment();

  const [hovered, setHovered] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [checkingId, setCheckingId] = useState(null);

  const taskLocked = LOCKED_TASK_STATES.includes(task.state);

  useEffect(() => {
    if (task) listTaskEquipment(task.task_id).then(() => setLoaded(true));
  }, [task]);

  useEffect(() => {
    if (loaded) onCountChange?.(equipment.length);
  }, [loaded, equipment.length, onCountChange]);

  // Group equipment by room
  const equipmentByRoom = useMemo(
    () => groupEquipmentByRoom(equipment, t('tasks:equipment.no_room', { defaultValue: 'No Room' })),
    [equipment, t],
  );

  const handleAddEquipment = async ({ equipments, relation_type, notes, rotation_label, rotation_options }) => {
    setLinkLoading(true);
    setLinkError(null);

    for (const equipment_id of equipments) {
      const result = await linkEquipmentToTask(task.task_id, {
        equipment_id,
        relation_type,
        notes,
        rotation_label,
        rotation_options,
      });
      if (!result.success) {
        setLinkError(result.error || t('common:messages.error_occurred'));
        setLinkLoading(false);
        return;
      }
    }

    onAddClose?.();
    await listTaskEquipment(task.task_id);
    setLinkLoading(false);
  };

  const handleDeleteEquipment = (taskId, taskEquipmentId, equipmentName) => {
    showConfirmDialog({
      title: t('tasks:equipment.remove_title'),
      message: t('tasks:equipment.confirm_remove', { name: equipmentName }),
      confirmText: t('common:buttons.remove'),
      severity: 'error',
      onConfirm: async () => {
        const result = await unlinkEquipmentFromTask(taskId, taskEquipmentId);
        if (result.success) await listTaskEquipment(task.task_id);
      },
    });
  };

  const handleToggleChecked = async (item, checked) => {
    setCheckingId(item.task_equipment_id);
    await setEquipmentChecked(task.task_id, item.task_equipment_id, checked);
    setCheckingId(null);
  };

  const handleSubmitEdit = async (data) => {
    setEditLoading(true);
    setEditError(null);

    const result = await updateTaskEquipment(task.task_id, editingLink.task_equipment_id, data);

    if (!result.success) {
      setEditError(result.error || t('common:messages.error_occurred'));
      setEditLoading(false);
      return;
    }

    setEditingLink(null);
    setEditLoading(false);
  };

  const linkedEquipmentIds = equipment.map(item => item.equipment.equipment_id);

  const showCard = Boolean(error) || equipment.length > 0;

  return (
    <>
      {showCard && (
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          ...props.sx,
        }}
      >
        <CardContent>
          <SectionHeader
            icon={Biotech}
            title={t('tasks:equipment.title')}
            actions={!taskLocked && canModifyEquipment && (
              <Button startIcon={<AddIcon />} onClick={onAddOpen} size="small" variant="outlined">
                {t('tasks:equipment.add_equipment')}
              </Button>
            )}
          />

          {error && <Alert severity="error">{t('common:messages.error_loading')}</Alert>}

          {!error && (
              <Stack spacing={3}>
                {equipmentByRoom.map(({ roomId, ...roomData }) => (
                  <Box key={roomId}>
                    {/* Room Header */}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        mb: 1.5,
                        px: 1
                      }}
                    >
                      <RoomIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle1" fontWeight="medium">
                        {roomData.roomName}
                      </Typography>
                    </Box>

                    {/* Equipment in this room */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 2 }}>
                      {roomData.items.map((item) => {
                        const eq = item.equipment;
                        const isHovered = hovered === item.task_equipment_id;
                        const imageUrl = getEquipmentImageByModel(eq.model);

                        return (
                          <Box
                            key={item.task_equipment_id}
                            sx={{
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              border: 1,
                              borderColor: isHovered ? 'primary.main' : 'divider',
                              borderRadius: 2,
                              cursor: taskLocked || !canModifyEquipment ? 'default' : 'pointer',
                              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                              boxShadow: isHovered ? '0 2px 10px rgba(0,0,0,0.08)' : 'none',
                              bgcolor: 'background.paper',
                            }}
                            onClick={() => {
                              if (taskLocked || !canModifyEquipment || checkingId === item.task_equipment_id) return;
                              handleToggleChecked(item, !item.is_checked);
                            }}
                            onMouseEnter={() => setHovered(item.task_equipment_id)}
                            onMouseLeave={() => setHovered(null)}
                          >
                            <Tooltip
                              title={t('tasks:equipment.mark_done', { defaultValue: 'Mark as checked for this task' })}
                            >
                              <span>
                                <Checkbox
                                  checked={Boolean(item.is_checked)}
                                  disabled={taskLocked || !canModifyEquipment || checkingId === item.task_equipment_id}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => handleToggleChecked(item, e.target.checked)}
                                  sx={{ p: 0.5 }}
                                />
                              </span>
                            </Tooltip>

                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                flexShrink: 0,
                                borderRadius: 1.5,
                                border: 1,
                                borderColor: 'divider',
                                bgcolor: 'grey.50',
                                backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                              }}
                            />

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body1" fontWeight="medium" noWrap>
                                {eq.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {t(`equipment:equipment_types.${eq.equipment_type}`)}
                                {eq.model && ` • ${eq.model}`}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                                {item.rotation_options?.length > 0 && (
                                  <Tooltip
                                    title={
                                      item.is_checked
                                        ? t('tasks:equipment.rotation_next', {
                                            defaultValue: 'Next: {{value}}',
                                            value: item.rotation_options[
                                              (item.rotation_index + 1) % item.rotation_options.length
                                            ],
                                          })
                                        : ''
                                    }
                                  >
                                    <Chip
                                      icon={<SyncIcon />}
                                      label={`${item.rotation_label || t('tasks:equipment.rotation_label', { defaultValue: 'Rotation' })}: ${item.current_mark}`}
                                      size="small"
                                      color={item.is_checked ? 'primary' : 'default'}
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                )}
                                {/* Active is the normal case; only flag equipment that's out of use */}
                                {!eq.is_active && (
                                  <Chip
                                    icon={<BlockIcon />}
                                    label={t('equipment:status.inactive')}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>

                            {canModifyEquipment && (
                              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingLink(item);
                                    setEditError(null);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteEquipment(item.task_id, item.task_equipment_id, eq.name);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Stack>
          )}
        </CardContent>
      </Card>
      )}

      <ConfirmDialog />

      <AddEquipmentDialog
        open={Boolean(addOpen)}
        onClose={() => {
          onAddClose?.();
          setLinkError(null);
        }}
        onSubmit={handleAddEquipment}
        linkedEquipmentIds={linkedEquipmentIds}
        submitLoading={linkLoading}
        submitError={linkError}
      />

      <EditEquipmentDialog
        open={Boolean(editingLink)}
        onClose={() => {
          setEditingLink(null);
          setEditError(null);
        }}
        onSubmit={handleSubmitEdit}
        link={editingLink}
        submitLoading={editLoading}
        submitError={editError}
      />
    </>
  );
}